import { Suspense, lazy, useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { ApiClientError } from '@olmt/shared'
import { bootstrapSession } from '../lib/data'
import { accountGate } from '../lib/approval'
import { connectSocket, disconnectSocket } from '../lib/socket'
import { onServiceWorkerMessage } from '../lib/push'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { useSearchStore } from '../stores/searchStore'
import { IncomingCallRing } from '../components/IncomingCallRing'
import { CallNotice } from '../components/CallNotice'
import { ReloadPrompt } from '../components/ReloadPrompt'
import { WaitingApprovalPage } from './WaitingApprovalPage'

// A static import cannot work here: `React.lazy` needs the module to be a separate chunk so the
// ~700 kB LiveKit bundle loads only when a call actually starts.
const CallOverlay = lazy(async () => {
  const module = await import('../components/CallOverlay')
  return { default: module.CallOverlay }
})

export function RootLayout() {
  const token = useAuthStore((state) => state.token)
  const user = useAuthStore((state) => state.user)
  const location = useLocation()
  const navigate = useNavigate()

  /**
   * §11: only `approved` opens the API. Read from the session first and corrected by the waiting
   * screen's `GET /api/me`, so approval is the *only* thing that starts the socket, the session
   * bootstrap and the ring — that is what makes an approval land without a reload.
   */
  const approved = accountGate(user) === 'approved'

  const isPublicRoute = location.pathname === '/login' || location.pathname.startsWith('/i/')

  // `navigate` is a new function on some router updates; the ring bridge outlives those, so it
  // reads the current one through a ref instead of re-initialising (and re-ringing) each time.
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  useEffect(() => {
    if (!token || !approved) return
    connectSocket()
    bootstrapSession().catch((error) => {
      // Only a revoked device token ends the session; a network blip must not.
      if (error instanceof ApiClientError && error.code === 'unauthorized') {
        useAuthStore.getState().clearSession()
        useChatStore.getState().reset()
        useSearchStore.getState().clear()
      }
    })
    return () => disconnectSocket()
  }, [token, approved])

  // Native (Capacitor Android) ringing: caches the session for the notification actions and
  // registers the cold-start `ring` / ongoing-call `hangUp` listeners. No-op elsewhere.
  useEffect(() => {
    if (!token || !approved) return
  }, [token, approved])

  useEffect(
    () =>
      onServiceWorkerMessage((message) => {
        if (message.type !== 'notificationclick' || !message.url) return
        const url = new URL(message.url, window.location.origin)
        void navigate(`${url.pathname}${url.search}`)
      }),
    [navigate],
  )

  if (!token && !isPublicRoute) return <Navigate to="/login" replace />

  // Signed in but not approved: the waiting screen and nothing else — no socket, no chat list, no
  // ring, no call overlay. `GET /api/me` (its own poll) is the only request an account in this
  // state is allowed to make, and it is what flips this branch.
  if (token && !approved) return <WaitingApprovalPage />

  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <CallOverlay />
      </Suspense>
      <IncomingCallRing />
      <CallNotice />
      <ReloadPrompt />
    </>
  )
}
